import { useMemo } from 'react'

export type TableColumnAlign = 'left' | 'center' | 'right'

export type TableColumnDefinition<TColumnId extends string> = {
    id: TColumnId
    label: string
    /** What the header's tooltip reads, for a label too short to say it all. */
    tooltip?: string
    align?: TableColumnAlign
    width?: number
}

export type VisibleTableColumn<TColumn> = TColumn & { width: string }

const HEADER_CHARACTER_WIDTH = 10

const HEADER_CHROME_WIDTH = 70

/** A centred header is padded by the sort arrow's own width to balance it, so it costs twice. */
const CENTRED_HEADER_EXTRA_WIDTH = 26

const MIN_COLUMN_WIDTH = 120

export const getColumnWidth = ({ label, align, width }: Omit<TableColumnDefinition<string>, 'id'>) =>
    width ??
    Math.max(
        Math.round(label.length * HEADER_CHARACTER_WIDTH) +
            HEADER_CHROME_WIDTH +
            (align === 'center' ? CENTRED_HEADER_EXTRA_WIDTH : 0),
        MIN_COLUMN_WIDTH
    )

const NO_HIDDEN_COLUMN_IDS: readonly string[] = []

type UseTableColumnLayoutParams<TColumn extends TableColumnDefinition<string>> = {
    columns: TColumn[]
    hiddenColumnIds?: readonly string[]
    minTableWidth?: number
    reservedWidth?: number
    /** The scroller's own width, so the slack can be shared out here rather than by the browser. */
    availableWidth?: number
}

export const useTableColumnLayout = <TColumn extends TableColumnDefinition<string>>({
    columns,
    hiddenColumnIds = NO_HIDDEN_COLUMN_IDS,
    minTableWidth = 0,
    reservedWidth = 0,
    availableWidth = 0,
}: UseTableColumnLayoutParams<TColumn>) => {
    const visibleColumns = useMemo((): VisibleTableColumn<TColumn>[] => {
        const shown = columns.filter((column) => !hiddenColumnIds.includes(column.id))
        const asked = shown.map(getColumnWidth)
        const total = asked.reduce((sum, width) => sum + width, 0)
        const target = availableWidth - reservedWidth

        // A fixed-layout table shares its slack between every column, the index and the actions
        // among them, so a narrow table leaves the index fat. Widening the columns that hold
        // something to fill the table here leaves the browser no slack to hand the rest.
        if (!total || target <= total) {
            return shown.map((column, index) => ({ ...column, width: `${asked[index]}px` }))
        }

        const widths = asked.map((width) => Math.round((width * target) / total))
        const drift = target - widths.reduce((sum, width) => sum + width, 0)
        widths[widths.length - 1] += drift

        return shown.map((column, index) => ({ ...column, width: `${widths[index]}px` }))
    }, [columns, hiddenColumnIds, reservedWidth, availableWidth])

    const visibleColumnIds = useMemo(
        () => visibleColumns.map((column) => column.id) as TColumn['id'][],
        [visibleColumns]
    )

    const tableMinWidth = useMemo(() => {
        const total = columns
            .filter((column) => !hiddenColumnIds.includes(column.id))
            .reduce((sum, column) => sum + getColumnWidth(column), reservedWidth)

        return Math.max(total, minTableWidth)
    }, [columns, hiddenColumnIds, minTableWidth, reservedWidth])

    return {
        visibleColumns,
        visibleColumnIds,
        tableMinWidth,
    }
}

export default useTableColumnLayout
